import { ref, watch, onUnmounted } from 'vue';

import { parseISO } from 'date-fns';
import { throttle } from 'lodash';
import { useEventListener } from '@vueuse/core';
import { useAuthStore } from '@/stores/auth';
import { onSharedAuthToken } from '@/common/authTokenChannel';
import router from '@/router';
import Storage from 'vue-ls';
import VueCookies from 'vue-cookies';
import { useCookies } from '@vueuse/integrations/useCookies';
import IntervalWorker from '@/workers/intervalWorker.js?worker';
import TimeoutWorker from '@/workers/timeoutWorker.js?worker';

const lockOutCountDown = ref(null);

// User input only: scroll and focusin also fire from code (chat auto-scroll, dialog autofocus), which
// would keep an unattended session open. Page scroll happens inside IonContent's shadow root, where
// scroll events never reach window; wheel does. input catches dictation, which types no keys.
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'wheel', 'keydown', 'input'];

export function useLockout() {
    const authStore = useAuthStore();

    const { ls } = Storage.useStorage({
        namespace: '',
        name: 'ls',
        storage: 'local',
    });

    const cookies = useCookies();

    const changeListener = ({ name, value }) => {
        if (name === 'lockOutCountDown' && value) {
            if (lockoutCountdownActive.value) return;
            lockOutCountDown.value = value;
            if (!lockoutCountdownActive.value) {
                startLockoutTimer();
            }
        } else if (name === 'user' && !value) {
            if (authStore.acquireAuthRedirect()) {
                router.push({ name: 'login' });
            }
        } else if (name === 'account') {
            if (!value) {
                authStore.removeAccount();
                if (authStore.acquireAuthRedirect()) {
                    router.push({ name: 'login' });
                }
            } else {
                authStore.setAccountData(value);
                if (value?.id !== authStore.account.id) {
                    router.push({ name: 'invalid-tab' });
                }
            }
        }
    };

    cookies.addChangeListener(changeListener);

    const lockoutTimerWorker = new IntervalWorker();
    let lockoutIntervalActive = ref(false);
    let lockoutIntervalPaused = ref(false);

    // handle
    lockoutTimerWorker.onmessage = (e) => {
        if (e.data.type === 'check') {
            // Don't check if already paused or locked
            if (lockoutCountdownActive.value || authStore.isLockedOut) {
                return;
            }

            let timeToLockout = authStore.lockoutLastActivityTime;
            let lockoutTimeMs = authStore.lockoutTime;

            // Send the current time from worker to ensure consistency
            const currentTime = e.data.currentTime || Date.now();
            const expirationTime = timeToLockout.getTime() + lockoutTimeMs;
            const timeRemaining = expirationTime - currentTime;

            if (timeRemaining <= 0) {
                startLockoutTimer();
            }
        }
    };

    const pauseLockoutInterval = () => {
        lockoutTimerWorker.postMessage({ action: 'stop' });
        lockoutIntervalActive.value = false;
    };

    const resumeLockoutInterval = () => {
        lockoutTimerWorker.postMessage({ action: 'start', interval: 2500 });
        lockoutIntervalActive.value = true;
    };

    const countdownWorker = new IntervalWorker();

    const lockoutCountdownActive = ref(false);

    countdownWorker.onmessage = (e) => {
        if (e.data.type === 'check') {
            if (lockOutCountDown.value === null) return;
            lockOutCountDown.value -= 1;
            VueCookies.set('lockOutCountDown', lockOutCountDown.value);
            if (lockOutCountDown.value < 1) {
                lockOutCountDown.value = null;
                VueCookies.remove('lockOutCountDown');
                executeLockout();
            }
        }
    };

    const pauseLockoutCountdown = () => {
        countdownWorker.postMessage({ action: 'stop' });
        lockoutCountdownActive.value = false;
        lockOutCountDown.value = null;
        VueCookies.remove('lockOutCountDown');
    };

    const resumeLockoutCountdown = () => {
        countdownWorker.postMessage({ action: 'start' });
        lockoutCountdownActive.value = true;
    };

    const localStorageOnLastTokenRefreshTime = (lastTokenRefreshTime) => {
        authStore.setLastTokenRefreshTime(parseISO(lastTokenRefreshTime));
    };

    const localStorageOnLockoutLastActivityTime = (lockoutLastActivityTime) => {
        resetLockoutActivity(lockoutLastActivityTime);
    };

    const localStorageOnLockoutTriggered = () => {
        if (!authStore.isLockedOut) {
            executeLockout();
        }
    };

    ls.on('lastTokenRefreshTime', localStorageOnLastTokenRefreshTime);
    ls.on('lockoutLastActivityTime', localStorageOnLockoutLastActivityTime);
    ls.on('lockoutTriggered', localStorageOnLockoutTriggered);

    const stopSharedAuthTokenListener = onSharedAuthToken((token) => authStore.adoptSharedToken(token));

    const tokenRefreshWorker = new TimeoutWorker();
    const refreshTokenTimeout = 10 * 60000; // 10 mins, token expires in 15 mins, give 5 mins buffer

    tokenRefreshWorker.onmessage = async (e) => {
        if (e.data.type === 'check') {
            try {
                await authStore.refreshAuthToken();
            } catch {
                // refreshAuthToken retries transient failures internally;
                // if it still throws, the token will expire naturally and the
                // next navigation will trigger re-authentication.
            }
            startTokenRefreshTimer();
        }
    };

    const startTokenRefreshTimer = () => {
        const lastRefreshTimestamp = authStore.lastTokenRefreshTime?.getTime() || Date.now();
        const nextRefreshTimestamp = lastRefreshTimestamp + refreshTokenTimeout;
        const now = Date.now();
        const timeoutDuration = Math.max(0, nextRefreshTimestamp - now);

        tokenRefreshWorker.postMessage({
            action: 'start',
            timeout: timeoutDuration,
        });
    };

    const stopTokenRefreshTimer = () => {
        tokenRefreshWorker.postMessage({ action: 'stop' });
    };

    onUnmounted(() => {
        cookies.removeChangeListener(changeListener);

        ls.off('lastTokenRefreshTime', localStorageOnLastTokenRefreshTime);
        ls.off('lockoutLastActivityTime', localStorageOnLockoutLastActivityTime);
        ls.off('lockoutTriggered', localStorageOnLockoutTriggered);
        stopSharedAuthTokenListener();
        throttledResetLockoutActivity.cancel();

        lockoutTimerWorker.postMessage({ action: 'terminate' });
        countdownWorker.postMessage({ action: 'terminate' });
        tokenRefreshWorker.postMessage({ action: 'terminate' });

        // Clean up window property
        delete window.keepSessionAlive;
    });

    const resetLockoutActivity = (lockoutLastActivityTime = null) => {
        if (!authStore.isAuthenticated || authStore.isLockedOut) {
            return;
        }

        pauseLockoutCountdown();

        if (lockoutLastActivityTime) {
            authStore.setLockoutLastActivityTime(parseISO(lockoutLastActivityTime));
        } else {
            authStore.resetLockoutActivityTime();
        }

        if (!lockoutIntervalActive.value && !lockoutIntervalPaused.value) {
            resumeLockoutInterval();
        }
    };

    // Expose to window for 3rd party chrome extensions to prevent lockout
    window.keepSessionAlive = () => {
        resetLockoutActivity();
    };

    // Leading edge, so the first sign of the user dismisses the countdown at once
    const throttledResetLockoutActivity = throttle(() => resetLockoutActivity(), 2000);

    const onUserActivity = (e) => {
        // A browser can send a pointermove with no movement when the page changes under a still cursor
        if (e.type === 'pointermove' && e.movementX === 0 && e.movementY === 0) return;
        throttledResetLockoutActivity();
    };

    // Capture, so a component that stops an event's propagation can't hide it from the lockout
    useEventListener(window, ACTIVITY_EVENTS, onUserActivity, { passive: true, capture: true });

    const startLockoutTimer = () => {
        // if not logged in do nothing
        if (!authStore.isAuthenticated || authStore.isLockedOut || !authStore.lockoutLastActivityTime) {
            return;
        }

        pauseLockoutInterval();

        // open lockout snackbar
        lockOutCountDown.value = 30;
        VueCookies.set('lockOutCountDown', 30);

        // count down to auto-lock
        resumeLockoutCountdown();
    };

    const pauseLockout = () => {
        lockoutIntervalPaused.value = true;
        pauseLockoutInterval();
        pauseLockoutCountdown();
    };

    const resumeLockout = () => {
        lockoutIntervalPaused.value = false;
        resetLockoutActivity();
    };

    const executeLockout = () => {
        // if not logged in do nothing
        if (!authStore.isAuthenticated || authStore.isLockedOut || !authStore.lockoutLastActivityTime) {
            return;
        }
        // navigate to lockout page
        router.push({ name: 'lockout' });
    };

    watch(
        () => authStore.token,
        (newToken, oldToken) => {
            // If no token or on login/lockout pages, pause lockout
            if (!newToken) {
                pauseLockout();
                stopTokenRefreshTimer();
                return;
            }

            if (!oldToken) {
                resumeLockout();
            }
            startTokenRefreshTimer();
        },
        { immediate: true },
    );

    return {
        resetLockoutActivity,
        lockOutCountDown,
        pauseLockout,
        resumeLockout,
        stopTokenRefreshTimer,
    };
}
